import React from 'react'
import { Button } from '../../components/simple/button'
import { NumberSection } from '../../layouts/components/numberSection'
import { SpecialPanel } from '../../layouts/components/panel'
import { SimpleLayout } from '../../layouts/simple'
import { Teacher, useAppContext } from '../../providers/context'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../request'
import { useNavigate } from 'react-router-dom'
import { Loader } from '../../components/simple/loader'
import { BasicUser } from '../../components/userSelector/users'
import { UserSelector } from '../../components/userSelector/userSelector'
import { wrapSetState } from '../../utils'
import { Popups } from '../../providers/popups'
import { Checkbox } from '../../components/form/checkbox'
import { TextInput } from '../../components/form/textInput'
import { DistrictLogoUpload } from '../modals/districtSettings'

type OnboardingData = {
  schoolsToApproveAdmins: {
    id: number
    name: string
    existingAdmins: BasicUser[]
  }[]
  existingDistrictAdmins: BasicUser[]
  showLexikeetOptions: boolean
  allowSISEdits: boolean
  allowNonSISEdits: boolean
  allowLexikeet: boolean
  phoneNumber: string
  abbreviation: string
  logoLink?: string
}

export const DistrictOnboardingPage: React.FC<{ user: Teacher }> = ({ user }) => {
  const { data: onboardingData } = useRequest<OnboardingData>('/frontend_api/getDistrictOnboarding')

  return (
    <SimpleLayout>
      {onboardingData ? (
        <DistrictOnboardingPanel onboardingData={onboardingData} user={user} />
      ) : (
        <Loader className="mt-8" />
      )}
    </SimpleLayout>
  )
}

const DistrictOnboardingPanel: React.FC<{
  onboardingData: OnboardingData
  user: Teacher
}> = ({ onboardingData, user }) => {
  const { state, reloadUser } = useAppContext()
  const navigate = useNavigate()
  const [loading, setLoading] = React.useState(false)

  const [schoolAdmins, setSchoolAdmins] = React.useState<Record<number, BasicUser[]>>(() =>
    Object.fromEntries(
      onboardingData.schoolsToApproveAdmins.map((school) => [
        school.id,
        school.existingAdmins.map((user) => ({
          ...user,
          notToggleable: true,
        })),
      ])
    )
  )
  const [allowSISEdits, setAllowSISEdits] = React.useState(onboardingData.allowSISEdits)
  const [allowNonSISEdits, setAllowNonSISEdits] = React.useState(onboardingData.allowNonSISEdits)
  const [allowLexikeet, setAllowLexikeet] = React.useState(onboardingData.allowLexikeet)
  const [districtAdmins, setDistrictAdmins] = React.useState(() =>
    onboardingData.existingDistrictAdmins.map((user) => ({
      ...user,
      notToggleable: true,
    }))
  )
  const [phoneNumber, setPhoneNumber] = React.useState(onboardingData.phoneNumber)
  const [abbreviation, setAbbreviation] = React.useState(onboardingData.abbreviation)
  const [newLogoName, setNewLogoName] = React.useState<string | undefined>(undefined)
  const [logoUploading, setLogoUploading] = React.useState(false)

  const save = async () => {
    setLoading(true)

    let incompleteOnboardingNote: string | undefined = undefined
    if (Object.values(schoolAdmins).some((admins) => admins.length === 0)) {
      incompleteOnboardingNote = await Popups.prompt({
        title: 'Please add a note to support explaining why you have left the school admin field blank.',
        message: 'We will reach out to help address this issue. Put N/A if this is not needed.',
        confirm: 'Continue',
        cancel: 'Cancel',
      })
      if (!incompleteOnboardingNote || !incompleteOnboardingNote.trim()) {
        setLoading(false)
        return
      }
    }

    makeRequest('/frontend_api/completeDistrictOnboarding', {
      schoolAdmins: Object.fromEntries(
        Object.entries(schoolAdmins).map(([schoolId, admins]) => [schoolId, admins.map((user) => user.id)])
      ),
      allowSISEdits,
      allowNonSISEdits,
      allowLexikeet,
      districtAdmins: districtAdmins.map((user) => user.id),
      phoneNumber,
      abbreviation,
      newLogoName,
      incompleteOnboardingNote,
    })
      .then(() => reloadUser().then(() => navigate('/dashboard')))
      .catch(showAxiosErrorAsToast)
      .finally(() => setLoading(false))
  }
  return (
    <SpecialPanel className="mb-8 sm:!w-[650px]">
      <div className="text-3xl font-semibold">Wecome to {state.appName}!</div>
      <div className="-mt-1">Please complete a few quick steps to configure your district.</div>
      <NumberSection
        className="my-4"
        number={1}
        title={'Assign a principal or other administrator for your schools'}
        subtitle={'This person will be tasked with completing the onboarding process for their school.'}
      >
        {onboardingData.schoolsToApproveAdmins.map((school) => (
          <React.Fragment key={school.id}>
            <div className="-mb-1 mt-3 font-bold">{school.name}</div>
            <UserSelector
              selectedUsers={schoolAdmins[school.id]}
              setSelectedUsers={wrapSetState(setSchoolAdmins, school.id)}
              url={`/frontend_api/districtOnboardingSchoolUserSearch/${school.id}`}
              placeholder="Search for a principal or administrator"
            />
          </React.Fragment>
        ))}
      </NumberSection>
      <NumberSection
        className="my-4"
        number={2}
        title={'Decide on district settings'}
        subtitle={'These settings cannot be easily changed after this onboarding process is complete.'}
      >
        <LabeledEditRow
          checked={allowSISEdits}
          label={'Allow non-admin users to edit family info that was imported from your SIS?'}
          description={'Admins can always edit this info.'}
          onChecked={setAllowSISEdits}
          disabled={!allowNonSISEdits}
        />
        <LabeledEditRow
          checked={allowNonSISEdits}
          label={'Allow non-admin users to edit family info that was not imported from your SIS?'}
          description={'Admins can always edit this info.'}
          onChecked={setAllowNonSISEdits}
          disabled={allowSISEdits}
        />
        {onboardingData.showLexikeetOptions && (
          <LabeledEditRow
            checked={allowLexikeet}
            label={'Allow non-admin users to use integrated Lexikeet interpretation services?'}
            description={'Admins can always use Lexikeet when it is enabled at their schools.'}
            onChecked={setAllowLexikeet}
          />
        )}
      </NumberSection>
      <NumberSection
        className="my-4"
        number={3}
        title={'Appoint other district administrators'}
        subtitle={'Names initially shown below have already been granted admin permissions.'}
      >
        <UserSelector
          selectedUsers={districtAdmins}
          setSelectedUsers={setDistrictAdmins}
          url="/frontend_api/districtOnboardingUserSearch"
          placeholder="Search for a district admin"
        />
      </NumberSection>
      <NumberSection
        className="my-4"
        number={4}
        title={'Set the phone number that will be used to send messages from your district'}
      >
        <TextInput className="rounded-full" value={phoneNumber} onChange={setPhoneNumber} placeholder="xxx-xxx-xxxx" />
      </NumberSection>
      <NumberSection
        className="my-4"
        number={5}
        title={'Set the abbreviation that will be used to refer to this district in outgoing messages.'}
        subtitle={
          'This should be a short abbreviation of your district name. Leave this field as-is if you are satisfied with the default shown below.'
        }
      >
        <TextInput
          className="rounded-full"
          value={abbreviation}
          onChange={setAbbreviation}
          placeholder="District Abbreviation"
        />
      </NumberSection>
      <NumberSection className="my-4" number={6} title="Add a logo for your district (optional)">
        <DistrictLogoUpload
          logoLink={onboardingData.logoLink}
          uploadDisabled={loading}
          logoUploading={logoUploading}
          setLogoUploading={setLogoUploading}
          setNewLogoName={setNewLogoName}
        />
        {newLogoName && (
          <div className="text-sm text-logo">
            <b>Press "Save"</b> to finish updating your logo to "{newLogoName}"
          </div>
        )}
      </NumberSection>
      <Button logo onClick={save} disabled={loading || logoUploading} showLoader={loading}>
        Save and Continue
      </Button>
    </SpecialPanel>
  )
}

export const LabeledEditRow: React.FC<{
  checked: boolean
  label: string
  description?: string
  onChecked: (checked: boolean) => void
  disabled?: boolean
}> = ({ checked, label, description, onChecked, disabled }) => {
  return (
    <div
      className={`flex flex-row items-center justify-between rounded bg-slate-50 px-2 py-1 ${disabled ? 'pointer-events-none opacity-50' : ''}`}
    >
      <div className="flex flex-col">
        <span>{label}</span>
        {description && <small>{description}</small>}
      </div>
      <Checkbox checked={checked} onChecked={onChecked} />
    </div>
  )
}
